import { useEffect, useState } from 'react';
import api, { descargarReporte } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { crcExacto, cobraIvaAparte, LEYENDA_IVA_INCLUIDO, fechaLegal, fecha, METODOS_PAGO } from '../utils/format.js';
import { Icon } from './Icons.jsx';

/** DATE de MySQL (YYYY-MM-DD): sin timezone, para no correr el dia. */
function fechaDia(fecha) {
  if (!fecha) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(fecha));
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}-${p(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`;
}

const nombreMetodo = (m) => METODOS_PAGO.find((x) => x.value === m)?.label || m || '';

// Vista de comprobante tipo ticket. Sirve para imprimir, mostrar QR y enviar por correo.
export default function ComprobanteView({ venta, onClose, empresa: empresaProp }) {
  const toast = useToast();
  const [qr, setQr] = useState(null);
  const [empresa, setEmpresa] = useState(empresaProp || null);
  const [email, setEmail] = useState(venta.cliente?.email || '');
  const [enviando, setEnviando] = useState(false);
  const [bajando, setBajando] = useState(false);

  useEffect(() => {
    api.get(`/ventas/${venta.id}/qr`).then((r) => setQr(r.data.data.qr)).catch(() => {});
    if (!empresaProp) api.get('/empresa').then((r) => setEmpresa(r.data.data)).catch(() => {});
  }, [venta.id]);

  const imprimir = () => {
    const contenido = document.getElementById('ticket-imprimible').innerHTML;
    const w = window.open('', '_blank', 'width=380,height=640');
    w.document.write(`<html><head><title>Comprobante ${venta.numero}</title>
      <style>body{font-family:'Courier New',monospace;font-size:12px;padding:16px;color:#000}
      table{width:100%;border-collapse:collapse}td{padding:2px 0}
      .r{text-align:right}.c{text-align:center}.b{font-weight:bold}
      hr{border:none;border-top:1px dashed #999;margin:8px 0}img{max-width:140px}</style>
      </head><body>${contenido}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };

  const descargarPdf = async () => {
    setBajando(true);
    try {
      await descargarReporte(`/ventas/${venta.id}/pdf`, `${venta.fe_consecutivo || venta.numero}.pdf`);
    } catch {
      toast.error('No se pudo generar el PDF');
    } finally {
      setBajando(false);
    }
  };

  const enviarCorreo = async () => {
    if (!email) { toast.error('Ingresa un correo'); return; }
    setEnviando(true);
    try {
      const { data } = await api.post(`/ventas/${venta.id}/correo`, { email });
      if (data.data.simulado) toast.info('Correo simulado (configura SMTP para envios reales)');
      else toast.success('Comprobante enviado a ' + email);
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo enviar');
    } finally {
      setEnviando(false);
    }
  };

  const etiquetaTipo = {
    ticket: 'COMPROBANTE INTERNO',
    tiquete_electronico: 'TIQUETE ELECTRONICO',
    factura_electronica: 'FACTURA ELECTRONICA',
    factura_exportacion: 'FACTURA DE EXPORTACION',
    proforma: 'PROFORMA',
  }[venta.tipo_comprobante] || 'COMPROBANTE';

  // Credito: el papel tiene que decir que se fio, que se abono y que se debe.
  const esCredito = venta.condicion_venta === '02';
  const abonos = venta.cobros || [];
  const abonado = Number(venta.monto_pagado || 0);
  const saldo = venta.saldo ?? Math.max(Number(venta.total || 0) - abonado, 0);

  return (
    <div>
      <div id="ticket-imprimible" style={{
        fontFamily: "'Courier New', monospace", fontSize: 13, background: '#fff', color: '#000',
        padding: 18, borderRadius: 10, border: '1px solid var(--border)', maxWidth: 320, margin: '0 auto',
      }}>
        <div className="c" style={{ textAlign: 'center' }}>
          {empresa?.logo_url && (
            <img src={empresa.logo_url} alt=""
              style={{ maxWidth: 150, maxHeight: 70, objectFit: 'contain', marginBottom: 6 }} />
          )}
          <div className="b" style={{ fontWeight: 700, fontSize: 15 }}>{empresa?.nombre_comercial || 'Mi Negocio'}</div>
          {empresa?.razon_social && <div style={{ fontSize: 11 }}>{empresa.razon_social}</div>}
          {empresa?.identificacion && <div style={{ fontSize: 11 }}>Ced: {empresa.identificacion}</div>}
          {empresa?.telefono && <div style={{ fontSize: 11 }}>Tel: {empresa.telefono}</div>}
          <div style={{ fontSize: 11, marginTop: 4, fontWeight: 700 }}>{etiquetaTipo}</div>
          {esCredito && (
            <div style={{ fontSize: 12, marginTop: 3, fontWeight: 700, letterSpacing: '0.04em' }}>VENTA A CREDITO</div>
          )}
        </div>
        <hr style={{ border: 'none', borderTop: '1px dashed #999', margin: '8px 0' }} />
        <div style={{ fontSize: 11 }}>
          {/* El consecutivo de Hacienda son 20 digitos; el numero interno es otra cosa */}
          <div>Consecutivo: {venta.fe_consecutivo || venta.numero}</div>
          {venta.fe_consecutivo && <div>Interno: {venta.numero}</div>}
          <div>Fecha: {fechaLegal(venta.fecha)}</div>
          {venta.cliente?.nombre && <div>Cliente: {venta.cliente.nombre}</div>}
          {venta.fe_clave && (
            <div style={{ marginTop: 3 }}>
              Clave:<br />
              <span style={{ wordBreak: 'break-all', letterSpacing: '0.02em' }}>
                {venta.fe_clave.replace(/(.{10})/g, '$1 ').trim()}
              </span>
            </div>
          )}
        </div>
        <hr style={{ border: 'none', borderTop: '1px dashed #999', margin: '8px 0' }} />
        <table style={{ width: '100%' }}>
          <tbody>
            {(venta.items || []).map((it) => (
              <tr key={it.id}>
                <td style={{ verticalAlign: 'top' }}>
                  {it.cantidad} x {it.nombre}<br />
                  <span style={{ fontSize: 10 }}>{crcExacto(it.precio_unit)}</span>
                </td>
                <td className="r" style={{ textAlign: 'right', verticalAlign: 'top' }}>{crcExacto(it.total_linea)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <hr style={{ border: 'none', borderTop: '1px dashed #999', margin: '8px 0' }} />
        <table style={{ width: '100%' }}>
          <tbody>
            <tr><td>Subtotal</td><td className="r" style={{ textAlign: 'right' }}>{crcExacto(venta.subtotal)}</td></tr>
            {Number(venta.descuento) > 0 && <tr><td>Descuento</td><td className="r" style={{ textAlign: 'right' }}>-{crcExacto(venta.descuento)}</td></tr>}
            {cobraIvaAparte(venta.tipo_comprobante) && (
              <tr><td>IVA</td><td className="r" style={{ textAlign: 'right' }}>{crcExacto(venta.impuesto)}</td></tr>
            )}
            <tr className="b" style={{ fontWeight: 700, fontSize: 15 }}><td>TOTAL</td><td className="r" style={{ textAlign: 'right' }}>{crcExacto(venta.total)}</td></tr>
          </tbody>
        </table>
        {esCredito ? (
          <>
            <div style={{ fontSize: 11, marginTop: 6 }}>
              Condicion: Credito{venta.plazo_credito ? ` a ${venta.plazo_credito} dias` : ''}
            </div>
            {venta.fecha_vencimiento && (
              <div style={{ fontSize: 11, marginTop: 2 }}>Vence: {fechaDia(venta.fecha_vencimiento)}</div>
            )}
            <hr style={{ border: 'none', borderTop: '1px dashed #999', margin: '8px 0' }} />
            <div className="b" style={{ fontWeight: 700, fontSize: 11, marginBottom: 2 }}>ABONOS</div>
            <table style={{ width: '100%', fontSize: 11 }}>
              <tbody>
                {abonos.length === 0 ? (
                  <tr><td>Sin abonos</td><td className="r" style={{ textAlign: 'right' }}>{crcExacto(0)}</td></tr>
                ) : abonos.map((ab) => (
                  <tr key={ab.id}>
                    <td>{fecha(ab.created_at)} · {nombreMetodo(ab.metodo)}</td>
                    <td className="r" style={{ textAlign: 'right' }}>{crcExacto(ab.monto)}</td>
                  </tr>
                ))}
                <tr><td>Total abonado</td><td className="r" style={{ textAlign: 'right' }}>{crcExacto(abonado)}</td></tr>
                <tr className="b" style={{ fontWeight: 700, fontSize: 14 }}>
                  <td>SALDO PENDIENTE</td><td className="r" style={{ textAlign: 'right' }}>{crcExacto(saldo)}</td>
                </tr>
              </tbody>
            </table>
          </>
        ) : (
          <div style={{ fontSize: 11, marginTop: 6 }}>Pago: {nombreMetodo(venta.metodo_pago)}</div>
        )}
        {qr && <div className="c" style={{ textAlign: 'center', marginTop: 10 }}><img src={qr} alt="QR" style={{ maxWidth: 130 }} /></div>}
        {/* Sin comprobante electronico el impuesto no se cobra aparte. */}
        {!cobraIvaAparte(venta.tipo_comprobante) && (
          <div className="c" style={{ textAlign: 'center', fontSize: 10, marginTop: 8, fontStyle: 'italic' }}>
            {LEYENDA_IVA_INCLUIDO}
          </div>
        )}
        <div className="c" style={{ textAlign: 'center', fontSize: 11, marginTop: 8 }}>{empresa?.mensaje_factura || 'Gracias por su compra'}</div>

        {/* Pie legal de la representacion grafica */}
        <hr style={{ border: 'none', borderTop: '1px dashed #999', margin: '8px 0' }} />
        <div className="c" style={{ textAlign: 'center', fontSize: 8, lineHeight: 1.5 }}>
          <div>Emitida conforme a la Resolucion de Facturacion Electronica DGT-R-48-2016 del {fechaLegal(venta.fecha)}</div>
          <div className="b" style={{ fontWeight: 700, fontSize: 9 }}>Version Documento Electronico 4.4</div>
          <div className="b" style={{ fontWeight: 700 }}>Emitida conforme a la Resolucion de Facturacion Electronica Resolucion DGT-R-048-2016</div>
          <div>Powered by Growing Solutions CR</div>
        </div>
      </div>

      {/* Acciones (no se imprimen) */}
      <div style={{ marginTop: 16 }}>
        <div className="flex gap-sm" style={{ marginBottom: 10 }}>
          <button className="btn btn-ghost btn-block" onClick={imprimir}><Icon.print /> Imprimir</button>
          <button className="btn btn-ghost btn-block" onClick={descargarPdf} disabled={bajando}>
            {bajando ? <span className="spinner" /> : <><Icon.download /> PDF</>}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="input" type="email" placeholder="correo@cliente.cr" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="btn btn-primary" onClick={enviarCorreo} disabled={enviando} style={{ flexShrink: 0 }}>
            {enviando ? <span className="spinner" /> : <><Icon.mail /> Enviar</>}
          </button>
        </div>
      </div>
    </div>
  );
}
